import * as React from 'react';

// MUI
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';

import moment, { Moment } from 'moment';
import { getDateDiff } from 'utils/common';
import { useRegion } from 'common/hooks/region';
import { TERMINAL_SCREENSHOTS_TIMERANGE_LIMIT_DAYS } from '../config';
import { DateSearchSpec, EndDatePicker, ErrorType, StartDatePicker } from '../date-picker';
import { Mode } from '../ModeSelector';

/** The input values as returned by MUI */
interface State {
  startDate: Moment | null;
  endDate: Moment | null;
  startTime: string;
  endTime: string;
}

interface Props {
  /** Whether the user is searching by specific date or date range */
  mode: Mode;
  /**  Fired when the user has changed the date and time inputs into a valid configuration */
  search: (spec: DateSearchSpec | null) => void;
}

function getDateRangeRestrictionError(startDate: Moment, endDate: Moment): boolean {
  return Math.abs(getDateDiff(startDate, endDate)) > TERMINAL_SCREENSHOTS_TIMERANGE_LIMIT_DAYS;
}

/**
 * Renders the date and time inputs for the per diem reports page.
 * Supports two configurations, a specific date and a date range.
 */
export const DateAndTime: React.FC<Props> = (props) => {
  const region = useRegion();
  const { mode, search } = props;

  // Store the MUI values
  const [state, setState] = React.useState<State>({
    startDate:
      mode === 'date-range'
        ? moment().tz(region.timeZone).add(-1, 'days').startOf('day')
        : moment().tz(region.timeZone).startOf('day'),
    endDate: moment().tz(region.timeZone).startOf('day'),
    startTime: '12:00',
    endTime: '12:00',
  });

  // Store any error state
  const [error, setError] = React.useState<ErrorType>();

  // Determine which error to show if necessary
  let errorComponent: JSX.Element | null = null;
  if (error) {
    switch (error) {
      case 'date-range':
        errorComponent = <Alert severity="warning">Start date must be before end date</Alert>;
        break;
      case 'date-range-restriction':
        errorComponent = (
          <Alert severity="warning">
            Time range cannot be greater than {TERMINAL_SCREENSHOTS_TIMERANGE_LIMIT_DAYS} days
          </Alert>
        );
        break;
      default:
        break;
    }
  }

  const timezoneString = React.useMemo(() => {
    return moment().tz(region.timeZone).format('z');
  }, [region]);

  /**
   * This effect is the meat of the component and is responsible for setting any error state
   * and calling search when the inputs are in a valid configuration
   */
  React.useEffect(() => {
    const { startDate, endDate, startTime, endTime } = state;

    // Error checking!
    if (mode === 'date-range') {
      if (!startDate || !endDate || !startTime || !endTime) {
        // Don't continue if all values are not set, but don't visibly error either
        search(null);
        return;
      }

      if (startDate.isAfter(endDate)) {
        setError('date-range');
        search(null);
        return;
      }

      if (getDateRangeRestrictionError(startDate, endDate)) {
        setError('date-range-restriction');
        search(null);
        return;
      }
    }

    // If we made it this far clear any errors
    setError(undefined);

    // Create a search spec based on the mode and our input values
    let spec: DateSearchSpec | null = null;
    switch (mode) {
      case 'date-range':
        if (startDate && endDate) {
          spec = {
            startDate,
            startTime,
            endDate,
            endTime,
            mode,
          };
        }
        break;
      case 'specific-date':
        if (startDate && startTime) {
          spec = { date: startDate, time: startTime, mode };
        }
        break;
      default:
        break;
    }

    search(spec);
  }, [mode, state, search]);

  const handleStartTimeChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    setState((s) => ({ ...s, startTime: event.target.value }));
  };

  const handleEndTimeChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    setState((s) => ({ ...s, endTime: event.target.value }));
  };

  return (
    <div className="flex space-x-8">
      <div className="flex flex-col space-y-2">
        <h4 className="text-dark font-medium">
          {mode === 'specific-date' && 'Date'}
          {mode === 'date-range' && 'Start Date'}
        </h4>
        <StartDatePicker
          minDate={moment(region.terminalScreenshotsMinDate)}
          onChange={(startDate) => setState((s) => ({ ...s, startDate }))}
          initialValue={state.startDate}
        />
      </div>
      <div className="flex flex-col space-y-2">
        <h4 className="text-dark font-medium">Time in {timezoneString}</h4>
        <TextField
          id="startTime"
          type="time"
          value={state.startTime}
          onChange={handleStartTimeChange}
          inputProps={{
            style: {
              fontSize: 16,
            },
          }} // font size of input text
          sx={{
            width: `${mode === 'date-range' ? '150px' : '350px'}`,
            '& .MuiOutlinedInput-root': {
              minHeight: '58px',
            },
          }}
        />
      </div>
      {/* conditional end date */}
      {mode === 'date-range' && (
        <>
          <div className="flex flex-col space-y-2">
            <h4 className="text-dark font-medium">End Date</h4>
            <EndDatePicker
              onChange={(endDate) => setState((s) => ({ ...s, endDate }))}
              minDate={state.startDate}
              initialValue={state.endDate}
            />
            {errorComponent}
          </div>
          <div className="flex flex-col space-y-2">
            <h4 className="text-dark font-medium">Time in {timezoneString}</h4>
            <TextField
              id="endTime"
              type="time"
              value={state.endTime}
              onChange={handleEndTimeChange}
              inputProps={{
                style: {
                  fontSize: 16,
                },
              }} // font size of input text
              sx={{
                width: `${mode === 'date-range' ? '150px' : '350px'}`,
                '& .MuiOutlinedInput-root': {
                  minHeight: '58px',
                },
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};
DateAndTime.displayName = 'TerminalScreenshotsPage.DateAndTime';
